/* eslint-disable react/require-default-props */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { DefaultTFuncReturn } from 'i18next';
import { useState } from 'react';
import {
  Control,
  FieldErrorsImpl,
  FieldValues,
  Path,
  UnPackAsyncDefaultValues,
  UseFormRegister,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import DatePicker from '../../../atoms/DatePicker';
import MultiSelect from '../../../atoms/MultiSelect';
import SingleSelect from '../../../atoms/SingleSelect';
import ConfirmModal from '../../../modules/ConfirmModal';
import OrganizationTreeMultiSelect, { Option, OptionType } from '../../../modules/OrganizationTreeMultiSelect';

interface InputGroupProps<T extends FieldValues> {
  label: DefaultTFuncReturn;
  name: Path<UnPackAsyncDefaultValues<T>>;
  errors: Partial<
    FieldErrorsImpl<{
      [x: string]: any;
    }>
  >;
  isDisabled?: boolean | undefined;
  register: UseFormRegister<T>;
}

/**
 * ラベル付きテキスト入力
 * @param param0
 * @returns
 */
export function InputGroup<T extends FieldValues>({ label, name, errors, isDisabled, register }: InputGroupProps<T>) {
  const message = errors[name]?.message;

  return (
    <>
      <li>
        <label htmlFor={name}>{label}</label>
        <span>
          <input type="text" id={name} {...register(name)} disabled={isDisabled} />
        </span>
      </li>
      {message != null && (
        <li>
          <span />
          <span className="form-error">{message.toString()}</span>
        </li>
      )}
    </>
  );
}

InputGroup.defaultProps = {
  isDisabled: undefined,
};

interface SingleSelectGroupProps<S extends FieldValues, T> {
  label: DefaultTFuncReturn;
  name: Path<UnPackAsyncDefaultValues<S>>;
  allowEmpty: boolean;
  options: T[];
  errors: Partial<
    FieldErrorsImpl<{
      [x: string]: any;
    }>
  >;
  register: UseFormRegister<S>;
  getValue: (x: T) => string | number | undefined;
  getLabel: (x: T) => string | number | undefined;
}

/**
 * ラベル付き単数選択
 * @param param0
 * @returns
 */
export function SingleSelectGroup<S extends FieldValues, T>({
  label,
  name,
  allowEmpty,
  options,
  errors,
  register,
  getValue,
  getLabel,
}: SingleSelectGroupProps<S, T>) {
  const message = errors[name]?.message;

  return (
    <>
      <li>
        <label htmlFor={name}>{label}</label>
        <span>
          <SingleSelect
            id={name}
            name={name}
            allowEmpty={allowEmpty}
            options={options}
            register={register}
            getValue={getValue}
            getLabel={getLabel}
          />
        </span>
      </li>
      {message != null && (
        <li>
          <span />
          <span className="form-error">{message.toString()}</span>
        </li>
      )}
    </>
  );
}

interface MultiSelectGroupProps<S, T extends FieldValues> {
  label: DefaultTFuncReturn;
  name: Path<UnPackAsyncDefaultValues<T>>;
  options: S[];
  control: Control<T>;
  isDisabled: boolean;
  errors: Partial<
    FieldErrorsImpl<{
      [x: string]: any;
    }>
  >;
  getValue: (x: S) => number | undefined;
  getLabel: (x: S) => string | undefined;
}

/**
 * ラベル付き複数選択
 * @param param0
 * @returns
 */
export function MultiSelectGroup<S, T extends FieldValues>({
  label,
  name,
  options,
  control,
  isDisabled,
  errors,
  getValue,
  getLabel,
}: MultiSelectGroupProps<S, T>) {
  const message = errors[name]?.message;
  return (
    <>
      <li>
        <label htmlFor={name}>{label}</label>
        <span>
          <MultiSelect
            id={name}
            name={name}
            options={options}
            control={control}
            isDisabled={isDisabled}
            getValue={getValue}
            getLabel={getLabel}
          />
        </span>
      </li>
      {message != null && (
        <li>
          <span />
          <span className="form-error">{message.toString()}</span>
        </li>
      )}
    </>
  );
}

interface OrganizationTreeMultiSelectGroupProps<T extends FieldValues> {
  label: DefaultTFuncReturn;
  name: Path<UnPackAsyncDefaultValues<T>>;
  control: Control<T>;
  options: Option[];
  selectableTypes: OptionType[];
  isDisabled: boolean;
  errors: Partial<
    FieldErrorsImpl<{
      [x: string]: any;
    }>
  >;
}

/**
 * ラベル付き組織選択
 * @param param0
 * @returns
 */
export function OrganizationTreeMultiSelectGroup<T extends FieldValues>({
  label,
  name,
  control,
  options,
  selectableTypes,
  isDisabled,
  errors,
}: OrganizationTreeMultiSelectGroupProps<T>) {
  const message = errors[name]?.message;

  return (
    <>
      <li>
        <label htmlFor={name}>{label}</label>
        <span>
          <span className="dropdown">
            <OrganizationTreeMultiSelect
              id={name}
              name={name}
              control={control}
              options={options}
              selectableTypes={selectableTypes}
              isDisabled={isDisabled}
            />
          </span>
        </span>
      </li>
      {message != null && (
        <li>
          <span />
          <span className="form-error">{message.toString()}</span>
        </li>
      )}
    </>
  );
}

interface DatePickerProps {
  label: DefaultTFuncReturn;
  name: string;
  isDisabled: boolean;
  control: Control<FieldValues>;
  errors: Partial<
    FieldErrorsImpl<{
      [x: string]: any;
    }>
  >;
}

/**
 * ラベル付き DatePicker
 * @param param0
 * @returns
 */
export function DatePickerGroup({ label, name, isDisabled, control, errors }: DatePickerProps) {
  const message = errors[name]?.message;

  return (
    <>
      <li>
        <label htmlFor={name}>{label}</label>
        <span>
          <DatePicker id={name} name={name} isDisabled={isDisabled} control={control} displayMark />
        </span>
      </li>
      {message != null && (
        <li>
          <span />
          <span className="form-error">{message.toString()}</span>
        </li>
      )}
    </>
  );
}

interface CancelButtonProps {
  editable: boolean;
  cancel: () => void | Promise<void>;
}

/**
 * キャンセルボタン（確認モーダル付き）
 * @returns
 */
export function CancelButton({ editable, cancel }: CancelButtonProps) {
  // 言語情報
  const { t } = useTranslation();

  // 確認モーダルの表示
  const [displayConfirmModal, setDisplayConfirmModal] = useState(false);

  return (
    <>
      {/* キャンセルボタン本体 */}
      <button type="button" className="text-button" onClick={() => setDisplayConfirmModal(true)} disabled={!editable}>
        <i className="fa-solid fa-circle-xmark" />
        {t('キャンセル')}
      </button>
      {/* 確認モーダル */}
      {displayConfirmModal && (
        <ConfirmModal
          onClickOk={() => {
            setDisplayConfirmModal(false);
            void cancel();
          }}
          onClickCancel={() => setDisplayConfirmModal(false)}
        >
          <div>{t('編集は破棄されます。よろしいですか？')}</div>
        </ConfirmModal>
      )}
    </>
  );
}
